import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { RequestDocuments, useRequestDocuments } from './useRequestDocuments';

type UseFormDocuments = [RequestDocuments, boolean, string?];

/**
 * A request's documents, wired into the surrounding react-hook-form.
 *
 * This is [[useRequestDocuments]] plus a form: use that one for read-only views (see
 * PaperworkCard), and this one only when a form field has to stay in step with the document list.
 * It must be called inside a FormProvider — `useFormContext` here is not optional.
 *
 * The form buys you two things:
 *
 * - Documents can come from the form instead of the API. Pass `formFieldName` with no `requestId`
 *   and the list is whatever that field holds, which is how a dropzone works before its request
 *   exists to attach uploads to.
 * - Documents fetched from the API are written back into that field. Without this, a document the
 *   other side just uploaded would reach whatever renders from this hook's return value, but not
 *   the form's other consumers reading the same field.
 *
 * @param requestId - Request to load documents for. Omit to read from the form field instead.
 * @param formFieldName - Field to mirror into, and to read from when there's no `requestId`.
 * @returns `[documents, isLoading, error]`
 */
export const useFormDocuments = ({
  requestId,
  formFieldName,
}: {
  requestId?: number;
  formFieldName?: string;
}): UseFormDocuments => {
  const { setValue } = useFormContext();
  const documentsFromForm = useWatch({ name: formFieldName });
  const [documentsFromDB, isLoading, error] = useRequestDocuments(requestId);
  const documents = (requestId ? documentsFromDB : documentsFromForm) || [];

  useEffect(() => {
    if (!requestId && !formFieldName) {
      console.warn('useFormDocuments needs either a requestId or a formFieldName to have anything to return.');
    }
  }, [requestId, formFieldName]);

  useEffect(() => {
    if (formFieldName && documentsFromDB.length) setValue(formFieldName, documentsFromDB);
  }, [documentsFromDB, formFieldName, setValue]);

  return [documents, isLoading, error];
};
